/**
 * Lectura de los impuestos y retenciones de un CFDI de compra y su identificacion contra
 * el catalogo de la empresa, para la captura rapida de gasto.
 *
 * Espeja a erp_backend/Utils/impuestosXmlCompra.utils.js. Si se cambia una regla de mapeo
 * aqui, hay que cambiarla alla tambien: el backend valida los ids que manda esta pantalla.
 *
 * La factura solo guarda el _id del catalogo, la base y el importe del XML. Las cuentas
 * contables salen del catalogo al contabilizar, y el importe nunca se vuelve a calcular.
 */

import round2Decimals from "../Components/round2Decimals";

// La tasa del CFDI SOLO sirve para elegir cual registro del catalogo es: el importe siempre
// es el del XML y nunca se recalcula. Por eso conviene ser tolerante con como la escribio el
// emisor en vez de tirar la factura completa.
//
// La retencion de IVA de las 2/3 partes es el caso tipico: llega como 0.106667 y en el
// catalogo esta como 10.6667, pero hay quien escribe 0.1067, 0.10666 o 0.106666667, y hasta
// quien la manda ya en porcentaje.
const TOLERANCIA_TASA = 0.0001; // coincidencia exacta
const TOLERANCIA_APROXIMADA = 0.5; // puntos porcentuales: absorbe el redondeo del emisor
// El segundo mejor candidato tiene que estar por lo menos esto de veces mas lejos. Si dos
// registros del catalogo quedan casi igual de cerca, no se adivina.
const FACTOR_AMBIGUEDAD = 3;

// Valores de Impuesto.tasaCuota. El 5 es el unico marcador de exento que existe: ni la
// Factura ni el XML guardan otra cosa que permita distinguirlo de un IVA 0%.
const TASA_CUOTA_SUBTOTAL = 1;
const TASA_CUOTA_ACUMULADO = 2;
const TASA_CUOTA_CUOTA = 3;
const TASA_CUOTA_ELEGIBLE = 4;
const TASA_CUOTA_EXENTO = 5;

const mismaTasa = (a, b) => Math.abs(Number(a) - Number(b)) < TOLERANCIA_TASA;

const codigoSatANumero = (codigo) => Number(String(codigo ?? "").trim());

export const nombreCodigoSat = (codigo) => {
  const numero = codigoSatANumero(codigo);
  if (numero === 1) return "001 - ISR";
  if (numero === 2) return "002 - IVA";
  if (numero === 3) return "003 - IEPS";
  return String(codigo);
};

/**
 * Registros del catalogo que comparten el codigo del SAT con lo que trae el XML. El codigo
 * por si solo no basta: una empresa puede tener IVA 16 e IVA 8, ambos con codigo 002.
 */
const candidatosPorCodigo = (catalogo, codigoSat) =>
  (catalogo || []).filter((valueRegistro) => Number(valueRegistro.impuesto) === codigoSat);

/**
 * Deja la tasa lista para mostrar: 16.666700 -> "16.6667", 16.0000 -> "16".
 */
export const formatearTasa = (tasa) => String(Number(Number(tasa).toFixed(4)));

const formatearDinero = (monto) =>
  `$${Number(monto || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const descripcionRegistro = (registro) =>
  registro.descripcionFactura || registro.nombre || "";

/**
 * Como se describe la tasa de un registro del catalogo al usuario. Un exento NO tiene tasa:
 * imprimirlo como "0%" es justo la confusion que hay que evitar, porque lo vuelve
 * indistinguible del registro de IVA 0% y los dos van a columnas distintas de la DIOT.
 */
export const etiquetaTasaRegistro = (registro) => {
  if (Number(registro.tasaCuota) === TASA_CUOTA_EXENTO) return "Exento";
  if (Number(registro.tasaCuota) === TASA_CUOTA_CUOTA) {
    return `cuota $${formatearTasa(registro.monto)}`;
  }
  return `${formatearTasa(registro.monto)}%`;
};

/**
 * Extrae los traslados y retenciones de un CFDI ya parseado a JSON.
 *
 * Los traslados se toman del nodo global `cfdi:Impuestos`, que trae TipoFactor y
 * TasaOCuota. Las retenciones se agregan desde los conceptos, porque el nodo global de
 * retenciones NO trae TasaOCuota y sin ella no se puede distinguir, por ejemplo, una
 * retencion de IVA de 2/3 de una de 6%. Al final se valida contra el total global.
 *
 * De cada nodo se conserva tambien la `Base`, para poder deducir la tasa efectiva
 * (importe/base) cuando la tasa declarada viene mal escrita o de plano no viene, y porque
 * es el unico dato con el que la DIOT puede separar un IVA 0% de un exento.
 *
 * @param {Object} comprobanteJson raiz del XML parseada por xmlToJson
 * @returns {{traslados: Array, retenciones: Array, avisos: Array}}
 */
export function leerImpuestosCfdi(comprobanteJson) {
  const hijos = comprobanteJson?.children || [];
  const nodoImpuestos = hijos.find((valueXml) => valueXml.name === "cfdi:Impuestos");
  const conceptos = hijos.find((valueXml) => valueXml.name === "cfdi:Conceptos")?.children || [];

  const avisos = [];

  //Traslados: del nodo global

  const traslados = [];
  const trasladosXml =
    nodoImpuestos?.children?.find((valueXml) => valueXml.name === "cfdi:Traslados")
      ?.children || [];

  for (const traslado of trasladosXml) {
    const attr = traslado.attributes || {};
    traslados.push({
      codigoSat: attr.Impuesto,
      tipoFactor: attr.TipoFactor || "Tasa",
      tasaOCuota: Number(attr.TasaOCuota || 0),
      // Solo existe desde CFDI 4.0; en 3.3 queda en 0 y simplemente no se usa
      base: Number(attr.Base || 0),
      importe: Number(attr.Importe || 0),
    });
  }

  //Retenciones: agregadas por concepto, para conservar la tasa

  const retencionesPorClave = new Map();

  for (const concepto of conceptos) {
    const retencionesConcepto =
      concepto.children
        ?.find((valueXml) => valueXml.name === "cfdi:Impuestos")
        ?.children?.find((valueXml) => valueXml.name === "cfdi:Retenciones")?.children || [];

    for (const retencion of retencionesConcepto) {
      const attr = retencion.attributes || {};
      const clave = `${attr.Impuesto}-${Number(attr.TasaOCuota || 0)}`;
      const previo = retencionesPorClave.get(clave);
      if (previo) {
        previo.importe = round2Decimals(previo.importe + Number(attr.Importe || 0));
        previo.base = round2Decimals(previo.base + Number(attr.Base || 0));
      } else {
        retencionesPorClave.set(clave, {
          codigoSat: attr.Impuesto,
          tasaOCuota: Number(attr.TasaOCuota || 0),
          base: Number(attr.Base || 0),
          importe: Number(attr.Importe || 0),
        });
      }
    }
  }

  const retenciones = Array.from(retencionesPorClave.values());

  //Cuadre contra el total retenido global del comprobante

  const totalRetenidoGlobal = Number(nodoImpuestos?.attributes?.TotalImpuestosRetenidos || 0);
  const totalRetenidoConceptos = retenciones.reduce(
    (acumulado, valueRetencion) => acumulado + valueRetencion.importe,
    0
  );

  if (round2Decimals(totalRetenidoGlobal) !== round2Decimals(totalRetenidoConceptos)) {
    avisos.push(
      "Las retenciones de los conceptos no suman el total retenido del comprobante. Revisa el desglose antes de guardar."
    );
  }

  return { traslados, retenciones, avisos };
}

/**
 * Elige el registro del catalogo cuya tasa se parece mas a la del XML.
 *
 * @returns {{registro: Object, aproximada: Boolean}|null} `aproximada` avisa que la tasa no
 *   cuadraba al 100% y se tuvo que interpretar.
 */
const elegirPorTasa = (candidatos, tasaPorcentaje) => {
  const tasa = Number(tasaPorcentaje);
  if (!Number.isFinite(tasa) || tasa < 0) return null;

  const ordenados = (candidatos || [])
    .map((registro) => ({
      registro,
      distancia: Math.abs(Number(registro.monto) - tasa),
    }))
    .filter((valueCandidato) => Number.isFinite(valueCandidato.distancia))
    .sort((a, b) => {
      // Se comparan las distancias ya cuantizadas para que el desempate sea consistente:
      // dos registros con el mismo `monto` tienen que quedar siempre empatados.
      const distanciaA = Math.round(a.distancia / TOLERANCIA_TASA);
      const distanciaB = Math.round(b.distancia / TOLERANCIA_TASA);
      if (distanciaA !== distanciaB) return distanciaA - distanciaB;
      // Empate: un traslado del CFDI trae su Base explicita, asi que "Tasa - Subtotal"
      // (tasaCuota 1) es su lectura canonica. Sin esto el ganador entre un IVA 0% y un
      // "IVA Exento" mal capturado (tasaCuota 2, monto 0) depende del orden de Mongo.
      return Number(a.registro.tasaCuota) - Number(b.registro.tasaCuota);
    });

  const mejor = ordenados[0];
  if (!mejor) return null;

  if (mejor.distancia < TOLERANCIA_TASA) {
    return { registro: mejor.registro, aproximada: false };
  }

  // Una tasa de 0 (IVA 0%) no se aproxima: o esta en el catalogo o no esta.
  if (tasa === 0) return null;

  const segundo = ordenados[1];
  if (
    mejor.distancia <= TOLERANCIA_APROXIMADA &&
    (!segundo || segundo.distancia > mejor.distancia * FACTOR_AMBIGUEDAD)
  ) {
    return { registro: mejor.registro, aproximada: true };
  }

  return null;
};

/**
 * Las distintas lecturas posibles de la tasa que trae el XML, de la mas confiable a la menos.
 */
const tasasPosibles = (tasaOCuota, importe, base) => {
  const tasa = Number(tasaOCuota || 0);
  const lista = [];

  // 1. Lo normal: el CFDI declara la tasa en fraccion (0.106667 = 10.6667%)
  if (Number.isFinite(tasa) && tasa >= 0) lista.push(tasa * 100);

  // 2. Hay emisores que la escriben ya en porcentaje (10.6667). Solo se intenta cuando pasa
  //    de 1, porque una tasa en fraccion nunca lo hace: si no, un 0.12 legitimo se leeria
  //    como "0.12%" y acabaria pegandole al registro de tasa 0.
  if (Number.isFinite(tasa) && tasa > 1) lista.push(tasa);

  // 3. Tasa efectiva: ignora por completo lo que declararon. Tambien salva al CFDI que no
  //    manda TasaOCuota, donde las dos lecturas anteriores valen 0.
  const baseNumero = Number(base || 0);
  const importeNumero = Number(importe || 0);
  if (baseNumero > 0 && importeNumero > 0) lista.push((importeNumero / baseNumero) * 100);

  return lista;
};

/**
 * Prueba las lecturas en orden y se queda con la primera que resuelva. Solo la primera (la
 * tasa tal como la declaro el emisor) cuenta como coincidencia limpia: si hubo que releer la
 * tasa de otra forma, el usuario tiene que enterarse.
 */
const buscarPorTasas = (candidatos, tasas) => {
  for (let index = 0; index < tasas.length; index++) {
    const encontrado = elegirPorTasa(candidatos, tasas[index]);
    if (encontrado) {
      return { registro: encontrado.registro, aproximada: encontrado.aproximada || index > 0 };
    }
  }
  return null;
};

/**
 * Registros del catalogo que pueden corresponder a un TipoFactor del CFDI.
 *
 * El fallback de "si solo hay un candidato usalo" TIENE que respetar esto. Sin el filtro, en
 * una empresa con un solo registro de codigo 002 el fallback casaba cualquier cosa con
 * cualquier cosa: un traslado Exento acababa guardado como IVA 16% (el defecto reportado) y,
 * al reves, un IVA 16% real acababa apuntando al registro Exento, que en el PDF sale sin
 * Importe y hace desaparecer el impuesto.
 */
const POOL_POR_TIPO_FACTOR = {
  Exento: [TASA_CUOTA_EXENTO],
  Cuota: [TASA_CUOTA_CUOTA],
  // El 4 (Elegible/combustible) no entra en la busqueda por tasa, asi que el fallback es la
  // unica via por la que llega a mapearse. La DIOT ya lo cuenta como IVA trasladado.
  Tasa: [TASA_CUOTA_SUBTOTAL, TASA_CUOTA_ACUMULADO, TASA_CUOTA_ELEGIBLE],
};

/**
 * Ultimo recurso: si en el catalogo hay un solo registro compatible con el TipoFactor del
 * XML, se usa ese aunque la tasa no haya cuadrado.
 *
 * Solo aplica cuando el renglon trae importe. El fallback existe para resolver *ambiguedad
 * de tasa* ("el emisor escribio 15.9 y el catalogo dice 16"); un renglon de importe 0 no
 * tiene tasa que aproximar: o es Exento (la tasa es irrelevante) o es tasa 0 (y
 * `elegirPorTasa` ya decidio a proposito no aproximarla). Adivinar ahi no resuelve nada,
 * solo inventa. Los casos legitimos de exento y 0% casan por coincidencia exacta.
 */
const fallbackUnicoCandidato = (candidatos, tipoFactor, nodo) => {
  if (Number(nodo.importe || 0) <= 0) return null;

  const tasasCuota = POOL_POR_TIPO_FACTOR[tipoFactor] || POOL_POR_TIPO_FACTOR.Tasa;
  const pool = candidatos.filter((valueRegistro) =>
    tasasCuota.includes(Number(valueRegistro.tasaCuota))
  );

  return pool.length === 1 ? { registro: pool[0], aproximada: true } : null;
};

/**
 * Busca el registro del catalogo de Impuestos que corresponde a un traslado del CFDI.
 *
 * Si la tasa no coincide con ninguno pero solo existe un registro compatible con ese
 * TipoFactor, se usa ese; el importe sigue siendo el del XML.
 *
 * @returns {{registro: Object, aproximada: Boolean}|null}
 */
export function buscarImpuestoCatalogo(catalogoImpuestos, traslado) {
  const codigoSat = codigoSatANumero(traslado.codigoSat);
  const tipoFactor = String(traslado.tipoFactor || "Tasa");
  const tasaOCuota = Number(traslado.tasaOCuota || 0);

  const candidatos = candidatosPorCodigo(catalogoImpuestos, codigoSat);

  if (candidatos.length === 0) return null;

  if (tipoFactor === "Exento") {
    const exento = candidatos.find(
      (valueImpuesto) => Number(valueImpuesto.tasaCuota) === TASA_CUOTA_EXENTO
    );
    // Sin registro exento se devuelve null a proposito, sin pasar por el fallback: un
    // exento que no existe en el catalogo se reporta, nunca se sustituye por otro IVA.
    return exento ? { registro: exento, aproximada: false } : null;
  }

  if (tipoFactor === "Cuota") {
    // Una cuota son pesos por unidad: aqui no aplica ninguna tolerancia, tiene que ser la misma.
    const cuota = candidatos.find(
      (valueImpuesto) =>
        Number(valueImpuesto.tasaCuota) === TASA_CUOTA_CUOTA &&
        mismaTasa(valueImpuesto.monto, tasaOCuota)
    );
    if (cuota) return { registro: cuota, aproximada: false };
    return fallbackUnicoCandidato(candidatos, tipoFactor, traslado);
  }

  const porTasa = candidatos.filter(
    (valueImpuesto) =>
      Number(valueImpuesto.tasaCuota) === TASA_CUOTA_SUBTOTAL ||
      Number(valueImpuesto.tasaCuota) === TASA_CUOTA_ACUMULADO
  );
  const encontrado = buscarPorTasas(
    porTasa,
    tasasPosibles(tasaOCuota, traslado.importe, traslado.base)
  );
  if (encontrado) return encontrado;

  return fallbackUnicoCandidato(candidatos, tipoFactor, traslado);
}

/**
 * Igual que buscarImpuestoCatalogo pero contra el catalogo de Retenciones. Las retenciones
 * del CFDI siempre son por tasa: una retención nunca puede ser Exento (CFDI40184) y el
 * catálogo tampoco ofrece cuota, así que el pool del fallback excluye tasaCuota 3 y 5.
 *
 * @returns {{registro: Object, aproximada: Boolean}|null}
 */
export function buscarRetencionCatalogo(catalogoRetenciones, retencion) {
  const codigoSat = codigoSatANumero(retencion.codigoSat);

  const candidatos = candidatosPorCodigo(catalogoRetenciones, codigoSat);

  if (candidatos.length === 0) return null;

  const encontrado = buscarPorTasas(
    candidatos,
    tasasPosibles(retencion.tasaOCuota, retencion.importe, retencion.base)
  );

  if (encontrado) return encontrado;

  return fallbackUnicoCandidato(candidatos, "Tasa", retencion);
}

/**
 * Acumula un importe sobre el renglon que ya exista para ese registro del catalogo. Un
 * mismo impuesto puede venir repartido en varios traslados del XML.
 *
 * @param {Number} base   Base declarada en el XML para ese renglón. Es el único dato con el
 *   que la DIOT puede separar un IVA 0% de un exento: los dos tienen importe 0.
 * @param {Object} campos { campoId, campoTotal, campoAjuste, campoBase }
 */
const acumular = (array, registro, importe, base, campos) => {
  const { campoId, campoTotal, campoAjuste, campoBase } = campos;
  const index = array.findIndex(
    (value) => value[campoId].toString() === registro._id.toString()
  );
  if (index === -1) {
    array.push({
      [campoId]: registro._id,
      descripcionFactura: registro.descripcionFactura,
      [campoTotal]: round2Decimals(importe),
      [campoBase]: round2Decimals(base),
      // El XML es el importe definitivo: no hay ajuste de centavos que aplicar.
      [campoAjuste]: 0,
    });
  } else {
    array[index][campoTotal] = round2Decimals(array[index][campoTotal] + importe);
    array[index][campoBase] = round2Decimals(array[index][campoBase] + base);
  }
};

/**
 * Las dos lecturas de la tasa que le sirven al usuario para entender que paso: la que
 * declaro el emisor y la que de verdad se cobro.
 */
const tasasDelXml = (nodo) => {
  const tasaOCuota = Number(nodo.tasaOCuota || 0);
  const base = Number(nodo.base || 0);
  const importe = Number(nodo.importe || 0);
  return {
    tasaXml: Number.isFinite(tasaOCuota) && tasaOCuota > 0 ? tasaOCuota * 100 : null,
    tasaEfectiva: base > 0 && importe > 0 ? (importe / base) * 100 : null,
  };
};

/**
 * Aviso legible cuando la tasa del XML no cuadraba y se tuvo que interpretar.
 */
const avisoTasaAproximada = (tipo, nodo, registro) => {
  const etiquetaTipo = tipo === "retencion" ? "retención" : "impuesto";
  const { tasaXml } = tasasDelXml(nodo);
  const comoVenia = tasaXml
    ? `venía con tasa ${formatearTasa(tasaXml)}% en el XML`
    : "venía sin una tasa utilizable en el XML";
  return (
    `La ${etiquetaTipo} ${nombreCodigoSat(nodo.codigoSat)} ${comoVenia}; se tomó ` +
    `"${descripcionRegistro(registro)}" (${etiquetaTasaRegistro(registro)}) del catálogo. ` +
    "El importe es el del XML."
  );
};

/**
 * Una retención en cero no mueve dinero: no hay importe que guardar ni que contabilizar. Hay
 * emisores que dejan el nodo puesto como plantilla cuando la retención no les aplica (típico
 * en paquetería: `<cfdi:Retencion TasaOCuota="0.000000" Importe="0.00"/>` porque la retención
 * de IVA de autotransporte no aplica). Con tasa 0 e importe 0 no hay forma de identificarla,
 * así que se ignora en vez de rechazar el XML completo por un renglón que no vale nada.
 *
 * **Solo aplica a retenciones.** Un traslado del nodo comprobante trae Base obligatoria
 * (CFDI40213) y es una declaración fiscal: un exento o un 0% que no se pueda identificar se
 * reporta, porque son justo los dos que la DIOT necesita separar.
 */
const sinImporte = (nodo) => Number(nodo.importe || 0) === 0;

const armarFaltante = (tipo, nodo, candidatos) => {
  const { tasaXml, tasaEfectiva } = tasasDelXml(nodo);
  return {
    tipo,
    codigoSat: nodo.codigoSat,
    etiqueta: nombreCodigoSat(nodo.codigoSat),
    tipoFactor: String(nodo.tipoFactor || "Tasa"),
    tasaOCuota: nodo.tasaOCuota,
    tasaXml,
    tasaEfectiva,
    base: Number(nodo.base || 0),
    importe: Number(nodo.importe || 0),
    candidatos: candidatos.map((valueRegistro) => ({
      descripcionFactura: descripcionRegistro(valueRegistro),
      tasaCuota: valueRegistro.tasaCuota,
      monto: valueRegistro.monto,
    })),
  };
};

/**
 * @returns {{arrayImpuestos: Array, arrayRetenciones: Array, faltantes: Array, avisos: Array}}
 *   arrayImpuestos:   [{ impuestoId, descripcionFactura, totalImpuestos, baseImpuesto, ajusteImpuesto }]
 *   arrayRetenciones: [{ retencionId, descripcionFactura, totalRetenciones, baseRetencion, ajusteRetencion }]
 */
export function resolverImpuestosXml(
  catalogoImpuestos,
  catalogoRetenciones,
  traslados,
  retenciones
) {
  const arrayImpuestos = [];
  const arrayRetenciones = [];
  const faltantes = [];
  const avisos = [];

  for (const traslado of traslados || []) {
    const importe = Number(traslado.importe || 0);
    const base = Number(traslado.base || 0);
    const encontrado = buscarImpuestoCatalogo(catalogoImpuestos, traslado);

    if (!encontrado) {
      // A diferencia de las retenciones, un traslado sin importe NO se ignora: un exento o
      // un 0% que no esté en el catálogo tiene que reportarse.
      faltantes.push(
        armarFaltante(
          "impuesto",
          traslado,
          candidatosPorCodigo(catalogoImpuestos, codigoSatANumero(traslado.codigoSat))
        )
      );
      continue;
    }

    if (encontrado.aproximada) {
      avisos.push(avisoTasaAproximada("impuesto", traslado, encontrado.registro));
    }

    acumular(arrayImpuestos, encontrado.registro, importe, base, {
      campoId: "impuestoId",
      campoTotal: "totalImpuestos",
      campoAjuste: "ajusteImpuesto",
      campoBase: "baseImpuesto",
    });
  }

  for (const retencion of retenciones || []) {
    const importe = Number(retencion.importe || 0);
    const base = Number(retencion.base || 0);
    const encontrado = buscarRetencionCatalogo(catalogoRetenciones, retencion);

    if (!encontrado) {
      if (sinImporte(retencion)) continue;
      faltantes.push(
        armarFaltante(
          "retencion",
          retencion,
          candidatosPorCodigo(catalogoRetenciones, codigoSatANumero(retencion.codigoSat))
        )
      );
      continue;
    }

    if (encontrado.aproximada) {
      avisos.push(avisoTasaAproximada("retencion", retencion, encontrado.registro));
    }

    acumular(arrayRetenciones, encontrado.registro, importe, base, {
      campoId: "retencionId",
      campoTotal: "totalRetenciones",
      campoAjuste: "ajusteRetencion",
      campoBase: "baseRetencion",
    });
  }

  // Un CFDI puede traer la misma tasa mal escrita en varios conceptos: se avisa una sola vez.
  return { arrayImpuestos, arrayRetenciones, faltantes, avisos: [...new Set(avisos)] };
}

/**
 * Mensaje listo para mostrar cuando no se pudo identificar algo del XML en el catálogo. Dice
 * qué traía el XML y qué hay en el catálogo, para poder dar de alta lo que falta o corregir
 * la tasa de una sola pasada.
 *
 * Un exento y un 0% necesitan su propia redacción: el texto por tasa no les aplica (un
 * exento no tiene tasa) y la instrucción de "corrige su tasa" los manda al lado equivocado.
 */
export function mensajeFaltantes(faltantes) {
  if (!faltantes || faltantes.length === 0) return null;

  const detalle = faltantes
    .map((valueFaltante) => {
      const tipo = valueFaltante.tipo === "retencion" ? "Retención" : "Impuesto";

      const enElCatalogo =
        !valueFaltante.candidatos || valueFaltante.candidatos.length === 0
          ? "la empresa no tiene ningún registro con ese código del SAT"
          : `en el catálogo hay ${valueFaltante.candidatos
              .map(
                (valueCandidato) =>
                  `${valueCandidato.descripcionFactura} (${etiquetaTasaRegistro(valueCandidato)})`
              )
              .join(", ")}`;

      const sobreLaBase =
        valueFaltante.base > 0
          ? ` sobre una base de ${formatearDinero(valueFaltante.base)}`
          : "";

      if (valueFaltante.tipoFactor === "Exento") {
        return (
          `${tipo} ${valueFaltante.etiqueta} exento${sobreLaBase}: ${enElCatalogo}. ` +
          `Falta un impuesto con Tasa o Cuota = "Exento"`
        );
      }

      if (valueFaltante.tipoFactor !== "Cuota" && !valueFaltante.tasaXml) {
        return (
          `${tipo} ${valueFaltante.etiqueta} con tasa 0% en el XML${sobreLaBase}: ` +
          `${enElCatalogo}. Falta un impuesto de tasa 0 (Tasa - Subtotal con monto 0)`
        );
      }

      let texto = `${tipo} ${valueFaltante.etiqueta}`;

      const partesTasa = [];
      if (valueFaltante.tasaXml) {
        partesTasa.push(`tasa ${formatearTasa(valueFaltante.tasaXml)}% en el XML`);
      }
      if (valueFaltante.tasaEfectiva) {
        partesTasa.push(
          `${formatearTasa(valueFaltante.tasaEfectiva)}% efectiva según importe/base`
        );
      }
      if (partesTasa.length > 0) texto += ` con ${partesTasa.join(" y ")}`;

      return `${texto}: ${enElCatalogo}`;
    })
    .join(" | ");

  return `El XML trae impuestos que no se pudieron identificar en el catálogo de la empresa. ${detalle}. Da de alta lo que falte o corrige su tasa, y vuelve a cargar el XML.`;
}
